"use client";

import { Phone, Plus, ChevronDown, ChevronUp, Clock, PhoneCall, MessageSquare } from "lucide-react";
import { useState, useEffect } from "react";
import { isNativePlatform } from "@/lib/capacitor/platform";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/app/_module/components/ui/collapsible";
import { usePhoneNumberManager } from "@/app/_module/hooks/usePhoneNumberPricing";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";

interface PhoneNumberPricingDisplayProps {
  showAddButton?: boolean;
  onAddPhoneNumber?: (areaCode: number, state: string) => void;
  compact?: boolean;
}

interface UsageSummary {
  totalCallMinutes: number;
  totalSMSSent: number;
  phoneNumbers: Array<{
    id: string;
    number: string;
    purchaseDate: string;
    lastBillingDate?: string;
    daysRemaining: number;
  }>;
}

// Hook to fetch usage summary
function useUsageSummary() {
  return useQuery({
    queryKey: ['phone-usage-summary'],
    queryFn: async () => {
      try {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: UsageSummary;
        }>({
          url: getApiUrl("/subscriptions/usage-summary"),
          auth: true,
        });

        // Handle different response formats
        const data = (response as any)?.data?.data || (response as any)?.data || null;

        if (!data) {
          // Return default structure if no data
          return {
            totalCallMinutes: 0,
            totalSMSSent: 0,
            phoneNumbers: [],
          };
        }

        return data as UsageSummary;
      } catch (error) {

        // Return default structure on error
        return {
          totalCallMinutes: 0,
          totalSMSSent: 0,
          phoneNumbers: [],
        };
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  });
}

// Calculate days remaining until next billing cycle
function calculateDaysRemaining(purchaseDate: string, lastBillingDate?: string): number {
  const referenceDate = lastBillingDate ? new Date(lastBillingDate) : new Date(purchaseDate);
  const nextBillingDate = new Date(referenceDate);
  nextBillingDate.setDate(nextBillingDate.getDate() + 30);

  const now = new Date();
  const diffTime = nextBillingDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

export function PhoneNumberPricingDisplay({
  showAddButton = false,
  onAddPhoneNumber,
  compact = false
}: PhoneNumberPricingDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Native app: hide the "$X per month" plan-pricing line (price advertising
  // without IAP is the thing reviewers flag; the user's own usage/billing
  // history below is plain account info and stays).
  const [isNativeApp, setIsNativeApp] = useState(false);
  useEffect(() => {
    setIsNativeApp(isNativePlatform());
  }, []);

  const {
    pricingInfo,
    isPricingLoading,
    pricingError,
    phoneNumbers,
    isPhoneNumbersLoading,
    phoneNumbersError,
    getPricingDisplay,
    canAddPhoneNumber,
    isUnlimited,
    hasFreeSlotsRemaining,
    isAssigning,
    isConfirming,
  } = usePhoneNumberManager();

  const { data: usageSummary, isLoading: isUsageLoading } = useUsageSummary();

  if (isPricingLoading || isPhoneNumbersLoading) {
    return (
      <Card className={compact ? "p-4" : ""}>
        <CardHeader className={compact ? "pb-2" : ""}>
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-3 w-48 bg-gray-200 rounded animate-pulse mt-2" />
        </CardHeader>
        <CardContent className={compact ? "pt-2" : ""}>
          <div className="h-16 w-full bg-gray-200 rounded animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  if (pricingError || phoneNumbersError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load phone number information. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  if (!pricingInfo) {
    return null;
  }

  const pricingDisplay = getPricingDisplay(pricingInfo);
  const currentCount = phoneNumbers.length;
  const freeCount = isUnlimited ? "Unlimited" : pricingInfo.freePhoneNumbers;

  // Enrich phone numbers with calculated days remaining
  const enrichedPhoneNumbers = phoneNumbers.map((phone: any) => {
    const daysRemaining = calculateDaysRemaining(
      phone.purchaseDate || phone.createdAt,
      phone.lastBillingDate
    );
    return {
      ...phone,
      daysRemaining,
    };
  });

  return (
    <Card className={compact ? "p-4" : ""}>
      <CardHeader className={compact ? "pb-2" : ""}>
        <CardTitle className="flex items-center gap-2">
          <Phone className="h-5 w-5" />
          Phone Number Plan
          {isUnlimited && (
            <Badge variant="secondary" className="ml-2">
              Unlimited
            </Badge>
          )}
        </CardTitle>
        {!isNativeApp && (
          <CardDescription>
            {pricingDisplay}
          </CardDescription>
        )}
      </CardHeader>

      <CardContent className={compact ? "pt-2" : ""}>
        <div className="space-y-4">
          {/* Current Phone Numbers Count */}
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Current Phone Numbers</p>
              <p className="text-2xl font-bold">{currentCount}</p>
            </div>
          </div>

          {/* Usage Statistics */}
          {!isUsageLoading && usageSummary && (
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <PhoneCall className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">Call Minutes</p>
                  <p className="text-sm font-semibold">{usageSummary.totalCallMinutes.toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">SMS Sent</p>
                  <p className="text-sm font-semibold">{usageSummary.totalSMSSent.toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}

          {/* Phone Number Expiration Details - Collapsible */}
          {enrichedPhoneNumbers.length > 0 && (
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between p-2 h-auto"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm font-medium">Billing Cycle Details</span>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <div className="space-y-2 rounded-lg border p-3 bg-muted/30">
                  {enrichedPhoneNumbers.map((phone: any) => {
                    const isExpiringSoon = phone.daysRemaining <= 7;
                    return (
                      <div
                        key={phone.id}
                        className="flex items-center justify-between py-2 border-b last:border-b-0"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {formatPhoneNumber(phone.number)}
                          </p>
                          {phone.areaCode && (
                            <p className="text-xs text-muted-foreground">
                              Area Code: {phone.areaCode}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p
                            className={`text-sm font-semibold ${
                              isExpiringSoon
                                ? "text-orange-600"
                                : "text-muted-foreground"
                            }`}
                          >
                            {phone.daysRemaining} days
                          </p>
                          <p className="text-xs text-muted-foreground">remaining</p>
                        </div>
                      </div>
                    );
                  })}
                  <div className="pt-2 mt-2 border-t">
                    <p className="text-xs text-muted-foreground">
                      Phone numbers renew every 30 days from purchase/last billing date
                    </p>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Add Phone Number Button */}
          {showAddButton && (
            <div className="pt-2">
              <Button
                onClick={() => onAddPhoneNumber?.(555, 'CA')}
                disabled={!canAddPhoneNumber || isAssigning || isConfirming}
                className="w-full"
                variant="default"
              >
                <Plus className="h-4 w-4 mr-2" />
                {isConfirming ? "Confirming..." : isAssigning ? "Adding..." : "Add Phone Number"}
              </Button>

              {!canAddPhoneNumber && (
                <p className="text-sm text-muted-foreground mt-2 text-center">
                  You have reached your phone number limit
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Compact version for use in settings or dashboard
export function PhoneNumberPricingCompact(props: Omit<PhoneNumberPricingDisplayProps, 'compact'>) {
  return <PhoneNumberPricingDisplay {...props} compact={true} />;
}

export default PhoneNumberPricingDisplay;
